<template>
    <div class="space-y-3">
        <section class="space-y-1">
            <form-label>Runs from</form-label>
            <p v-if="!runners.length" class="text-xs text-gray-300">Drag a wire from a trigger or condition to this action.</p>
            <div v-for="runner in runners" :key="runner.wire.id" class="flex items-center gap-1 text-xs">
                <input v-if="runners.length > 1" type="radio" :checked="runner.wire.id === activeRunner?.wire.id"
                    title="Edit in the context of this trigger" @change="chosenWireId = runner.wire.id" />
                <button class="min-w-0 flex-1 truncate text-left hover:text-ui-active"
                    @click="hass.select(page, 'node', runner.node.id)">{{ runner.label }}</button>
                <button class="text-red-300 hover:text-red-200" title="Disconnect" @click="hass.removeWire(page, runner.wire.id)">×</button>
            </div>
        </section>

        <TriggerRulesTable v-if="editorObject && meta" :key="editorKey" embedded
            :node="editorObject" :settings="settings" :models="models" :meta="meta"
            :widgetId="`p${page.haspid}b${editorObject.haspid}`" :pageId="page.haspid"
            :primaryEntity="primaryEntity" />
        <p v-else-if="runners.length" class="text-xs text-gray-300">The trigger of this action has no source object.</p>

        <section v-if="acceptsTarget" class="space-y-1">
            <form-label>Target entities</form-label>
            <p v-if="!targets.length" class="text-xs text-gray-300">
                {{ primaryEntity ? `Primary Entity ${primaryEntity}; wire the target output to an entity node to act on another.`
                    : 'Wire the target output to an entity node.' }}
            </p>
            <div v-for="target in targets" :key="target.wire.id" class="flex items-center justify-between gap-2 text-xs">
                <button class="truncate text-left font-mono hover:text-ui-active" @click="hass.select(page, 'node', target.wire.to.node)">
                    {{ target.entityId || '(no entity selected)' }}</button>
                <button class="text-red-300 hover:text-red-200" title="Disconnect" @click="hass.removeWire(page, target.wire.id)">×</button>
            </div>
        </section>
    </div>
</template>

<script setup>
import { computed, ref, toRaw } from 'vue';
import { useHassFlowStore } from '../../stores/hassFlowStore.js';
import { findNode, replaceNode } from '../../graph/graphModel.js';
import { getHassNodeSchema } from '../../Hasp/properties/v2/hass/index.js';
import {
    actionAcceptsTarget,
    getActionTargetWires,
    getActionTargets,
    getConditionTrigger,
    getTriggerSource,
    getWiresInto,
    setActionTargets,
} from '../../hass/hassGraph.js';
import { applyTargetEntities, extractTargetEntities } from '../../hass/hassTargets.js';
import { getEffectiveHassConfig } from '../../hass/hassEffective.js';
import { getEffectivePrimaryEntity } from '../../automation/primaryEntity.js';
import { getAutomationMetaByType } from '../../automation/schema/WidgetAutomationMeta.js';
import { AutomationModel } from '../../automation/ast/AutomationModel.js';
import {
    DEFAULT_TTS_LANGUAGE,
    DEFAULT_TTS_PROVIDER,
    DEFAULT_TTS_VOICE,
    parseTtsAction,
} from '../../automation/mediaPlayerTtsAction.js';
import { createTemplateDocument, createTextNode } from '../../automation/templateDocument.js';
import TriggerRulesTable from '../hassbuilder/automation/TriggerRulesTable.vue';
import { useNodeDraft } from './useNodeDraft.js';

/**
 * An action node, edited with the HASSBuilder's action editor (presets, text
 * to speech, media, service fields) in the context of a trigger that runs
 * it. Target entities are wires: entities chosen in the editor become wired
 * entity nodes when committed.
 */
const props = defineProps({
    page: { type: Object, required: true },
    graph: { type: Object, required: true },
    node: { type: Object, required: true },
    context: { type: Object, required: true },
    settings: { type: Object, default: () => ({}) },
});
const hass = useHassFlowStore();

// Triggers and conditions running this action, each with its trigger and source object.
const runners = computed(() => getWiresInto(props.graph, { node: props.node.id, port: 'run' })
    .map(wire => {
        const runner = findNode(props.graph, wire.from.node);
        const trigger = runner?.kind === 'condition' ? findNode(props.graph, getConditionTrigger(props.graph, runner.id)) : runner;
        const uuid = trigger ? getTriggerSource(props.graph, trigger.id) : null;
        const object = uuid ? props.context.getObject(uuid) : null;
        const via = runner?.kind === 'condition' ? ' (condition)' : '';
        const on = object ? ` on ${object.obj} p${props.page.haspid}b${object.haspid}` : '';
        return runner ? { wire, node: runner, trigger, object, label: `⚡ ${trigger?.data.event || '(no event)'}${on}${via}` } : null;
    })
    .filter(Boolean));
const chosenWireId = ref(null);
const activeRunner = computed(() => runners.value.find(item => item.wire.id === chosenWireId.value) ?? runners.value[0] ?? null);
const editorObject = computed(() => activeRunner.value?.object ?? null);
const meta = computed(() => (editorObject.value ? getAutomationMetaByType(editorObject.value.obj) : null));
// page.hass is plain data: depend on the graph prop so a Primary Entity wire change is seen.
const primaryEntity = computed(() => (props.graph && editorObject.value
    ? getEffectivePrimaryEntity(editorObject.value, { configOf: getEffectiveHassConfig }) : ''));

const targetIds = computed(() => getActionTargets(props.graph, props.node.id));
const targets = computed(() => getActionTargetWires(props.graph, props.node.id)
    .map(wire => ({ wire, entityId: findNode(props.graph, wire.to.node)?.data.entityId ?? '' })));
const acceptsTarget = computed(() => actionAcceptsTarget(props.node.data));

// The editor works on a one-rule model whose action carries the wired targets
// (see useNodeDraft); a commit writes the action's data and target wires.
function toModels() {
    const object = editorObject.value;
    const trigger = activeRunner.value?.trigger;
    const action = { ...applyTargetEntities(structuredClone(toRaw(props.node).data), targetIds.value), id: props.node.id };
    const models = [AutomationModel.fromJSON({
        id: props.node.id,
        widgetPageId: props.page.haspid,
        widgetHaspId: object?.haspid ?? null,
        widgetType: object?.obj ?? null,
        triggers: [{ id: trigger?.id, source: 'widget', event: trigger?.data.event ?? '', widgetId: object ? `p${props.page.haspid}b${object.haspid}` : null }],
        conditions: [],
        actions: [action],
    })];
    initTtsDraft(models[0].actions[0]);
    return models;
}

/**
 * The action editor recognizes text to speech by its target entity. A node
 * gets one only by a wire, so a new one starts from its metadata — as the
 * action editor's own Text to speech choice does.
 */
function initTtsDraft(action) {
    const meta = action.editorMetadata;
    if (meta?.operation !== 'tts' || parseTtsAction(action)) return;
    action._editorMode = 'media_player';
    action._mediaDraft = {
        entityId: targetIds.value[0] ?? '',
        operation: 'tts',
        message: meta.message ?? createTemplateDocument([createTextNode('')]),
        provider: meta.ttsProviderId ?? DEFAULT_TTS_PROVIDER,
        language: meta.language ?? DEFAULT_TTS_LANGUAGE,
        voice: meta.voice ?? DEFAULT_TTS_VOICE,
        isNew: true,
    };
    action._validationError = true;
}

/** Node data and target entities of the edited action. */
function split(action) {
    const json = JSON.parse(JSON.stringify(action.toJSON()));
    delete json.id;
    if (!actionAcceptsTarget(json)) return { data: json, targets: targetIds.value };
    const extracted = extractTargetEntities(json);
    return extracted ? { data: extracted.action, targets: extracted.entities } : { data: json, targets: [] };
}

const models = useNodeDraft({
    build: toModels,
    snapshot: value => (value[0]?.actions[0] ? split(value[0].actions[0]) : { data: props.node.data, targets: targetIds.value }),
    current: () => ({ data: props.node.data, targets: targetIds.value }),
    commit: ({ data, targets }) => {
        const node = toRaw(props.node);
        // A task type switch to or from Delay changes the node type (and its ports).
        const type = data.type === 'delay' ? 'delay' : 'service';
        let graph = replaceNode(toRaw(props.page).hass, { ...node, type, data });
        if (type !== node.type) {
            const { inputs, outputs } = getHassNodeSchema(type).ports;
            const has = (list, port) => list.some(item => item.id === port);
            graph = { ...graph, wires: graph.wires.filter(wire => (wire.to.node !== node.id || has(inputs, wire.to.port)) &&
                (wire.from.node !== node.id || has(outputs, wire.from.port))) };
        }
        if (type === 'service' && targets.join('\n') !== targetIds.value.join('\n')) graph = setActionTargets(graph, node.id, targets);
        hass.commit(props.page, graph, 'Edit HA flow node', { mergeKey: `data:${node.id}` });
    },
    rebuildOn: () => activeRunner.value?.wire.id ?? null,
});
const editorKey = computed(() => `${props.node.id}:${activeRunner.value?.wire.id ?? ''}`);
</script>
